/* ==========================================================================
   TNF pricing engine 2.7.0                                     2026-10-02
   2.7.0 = 2.6.9 plus a THIRD MARKET, the United Kingdom (theneonfactory.co.uk).
   AU and US are priced by exactly the 2.6.9 code paths (regression: every AU
   and US quote in the test matrix is identical to 2.6.9, field for field).
   UK rules (PROVISIONAL until Jack approves them in the UK launch review):
     - currency GBP; sizes in centimetres on the AU grid (1 cm steps, 480 cm);
     - retail = C_USD x UK multiplier (indoor 2.4, outdoor 2.8 - the outdoor one
       REPLACES the indoor one), giving GBP, VAT INCLUDED, never added on top;
     - C_USD is built exactly as for AU/US except freight: the UK column of the
       manufacturer's DHL card, which has NOT been supplied. Until it is, the
       US column stands in and every UK quote carries uk_freight_card_unconfirmed;
       no US prepaid import tax is charged on a UK sign;
     - fixed add-ons and the Premium RGB/RGBW table: the same NUMBER in pounds
       (the store's rule: same figure in each market's own currency, never
       converted);
     - grade P (records) designs: UK has no recorded selling prices of its own,
       so the AU records are scaled by UK indoor / AU indoor (2.4 / 4.5) and the
       quote carries uk_records_derived_from_au;
     - the promotion applies exactly as in AU/US.
   The geometry module knows two grids (AU cm, US in); a UK sign is measured on
   the AU grid and only its MARKET for freight, tax, multiplier and currency is
   UK - which is why manufacturer() now reads the quote's market, not g.market
   (identical for AU and US, where the two are always the same).
   ==========================================================================
   (2.6.9 header follows)
   TNF pricing engine 2.6.9                                     2026-09-26
   2.6.9 = 2.6.8 plus ONE catalogue-only pricing basis: 'records'.

   Jack, 26 Sep 2026: "Approve using each product's existing size-and-price
   records as the basis for a continuous retail model where the tube
   measurement remains uncertain ... It is a pricing policy, not a
   measurement." It applies ONLY when the geometry carries
   g.pricing.basis === 'records' (catalogue geometry 1.4.0, grade P designs).
   The creator's geometry never carries it, so every creator quote and every
   artwork-measured catalogue quote is priced exactly as 2.6.8 prices it.

   The rule (the simplest defensible one; all amounts in the market's own
   currency, AU and US separately):
     - anchors: g.pricing[market] = [[width_mm, price], ...] - the product's
       recorded sizes and selling prices (helper variants, erroneous records
       and promotions excluded upstream; the registry records the snapshot);
     - at a recorded width: the recorded price, exactly;
     - between two recorded widths: straight-line interpolation;
     - above the largest recorded width wL (price PL):
         PL + (retailIn(w) - retailIn(wL))
       where retailIn(x) = this engine's own manufacturing cost of the sign at
       width x (supplier matrix: tube, board, UV print, power, dimmer, carton
       from the geometry's packing plan, freight card, handling) x the
       market's indoor retail multiplier. Only the EXTRA cost of the larger
       sign is added, so nothing already in PL is charged twice, and the
       curve steps where the supplier's packaging and freight step;
     - outdoor, the same shape: at each recorded width, the recorded price x
       this engine's outdoor / indoor retail at that width (the supplier's
       outdoor tube, waterproof dimmer and outdoor supplies; never below the
       outdoor price of a smaller record); a straight line between; above the
       largest record, the outdoor price there + the supplier's extra OUTDOOR
       cost of the larger sign at the outdoor retail multiplier;
     - add-ons and the one whole-dollar rounding: unchanged.
   The cost model's tube figure (g.tubing.L_m, from costModel_m_at_ref) is an
   ESTIMATE used only for the growth and the outdoor ratio. It is never a
   measured length and the price never comes from it below wL.
   Anything the rule cannot price is refused with a named reason (never a
   guessed number): records_missing_for_market, records_not_increasing,
   below_recorded_range, records_growth_unpriceable,
   records_outdoor_unpriceable. quote.pricingBasis says which part of the
   rule priced the sign (internal; never customer text).
   ==========================================================================
   (2.6.8 header follows)
   TNF pricing engine 2.6.8                                     2026-09-25
   2.6.8 = 2.6.7 plus ONE more opt-in policy, 'catalogue-2', which the
   catalogue geometry adapter 1.2.0 sets instead of 'catalogue-1'.
   catalogue-2 = catalogue-1 (dimmer at exactly 5 m, outdoor above 12 m, UV
   print on the board area - unchanged) with ONE change: the cut plan is
   no longer this engine's own grid. The engine takes the sections the
   geometry adapter planned (g.parts.across x g.parts.down), so the customer
   notice, the freight, the cart line and the factory record all come from
   ONE plan. Jack, 25 Sep 2026: "where a sign needs splitting and is under
   1.5 metres, it should be supplied in TWO parts. Smaller signs that fit
   the creator's one-piece allowance remain one piece ... Do not introduce a
   separate grid that automatically turns catalogue signs into four or more
   pieces because of their proportions ... Do not promise a physically
   unsupported cut." The adapter's plan (tnf-cat-geometry-1-2-0.js header):
     - both sides within 110 cm (the creator's one-piece allowance): 1 piece;
     - needs splitting, longest side under 150 cm, and each half within the
       120 cm machine limit (Jack, 13 Aug 2024; PROVISIONAL): 2 pieces, cut
       across the longer side - the manufacturer's rule (23 Sep 2026) says
       two pieces of a board under 150 cm can be packed together;
     - anything else: the creator's own section grid, ceil(W / 110 cm)
       across x ceil(H / 110 cm) down (the creator's cap of 4 rows is not
       applied: it would leave sections taller than the machine limit on the
       few designs taller than 440 cm).
   Freight is still the ONE carton rule (8 cm of depth per section, the
   card extended above 18.5 kg). catalogue-1 and every creator quote are
   priced exactly as 2.6.7 prices them.
   ==========================================================================
   (2.6.7 header follows)
   TNF pricing engine 2.6.7                                     2026-09-25
   2.6.7 = 2.6.6 plus ONE opt-in hardware policy for catalogue designs.
   A geometry object that carries hardwarePolicy: 'catalogue-1' (only the
   catalogue geometry adapter tnf-cat-geometry-1-1-0.js sets it) is priced
   with three rules that 2.6.6 left as refusals or silent caps. ANY OTHER
   GEOMETRY - every creator quote - is priced exactly as 2.6.6 prices it
   (checked on creator configurations and on every catalogue width that
   2.6.6 already priced: identical results).
     1. DIMMER AT EXACTLY 5 m. The matrix lists a small dimmer "under 5 m"
        (US$3) and a large dimmer "over 5 m" (US$12); a tube that lands on
        5.000 m fits neither description. 2.6.6 refused the quote. The policy
        fits the LARGE dimmer - the part rated for longer runs, so it is not
        asked to do anything beyond its rating - and says so on the quote
        (provisional input dimmer_at_exactly_5m_large_fitted).
     2. OUTDOOR ABOVE 12 m OF TUBE. The matrix prices outdoor supplies to
        12 m (<6 m US$14.50, 6-12 m US$20.30) and says a longer sign "must be
        recalculated by the manufacturer". 2.6.6 refused the quote. The
        policy splits the tube into the fewest equal runs of at most 12 m
        (n = ceil(L / 12)) and gives each run its own supplied outdoor supply
        at that run's tier - only supplied parts, each inside its own supplied
        range. This mirrors the indoor rule the engine already applies above
        12 m (one supply per 12 m, 2.6.0) and, like it, keeps ONE dimmer. It is
        an ASSUMPTION about how the factory wires a long outdoor sign, carried
        on the quote as outdoor_power_supplies_over_12m_assumed, never
        presented as a manufacturer figure.
     3. SECTIONS ARE NEVER CAPPED. 2.6.6 capped the cut plan at 8 sections
        across and 4 down, so a board taller than 440 cm was planned in
        sections taller than the 110 cm one-piece limit. Catalogue designs
        can be that tall at 240 cm wide (Simple Flamingo 568 cm), so the
        policy plans ceil(W / 110 cm) across and ceil(H / 110 cm) down - every
        section inside the limit - and prices the carton through the same
        one-carton rule and the same extended card. The creator (max 480 cm
        wide, lettering) never reaches the old caps, so nothing changes there.
     4. UV-PRINTED ARTWORK. A catalogue design whose artwork has printed
        (UV) areas - the geometry says so (g.uvPrint: registry 1.4.0
        production.uvPrint, reviewed on the product photos or recorded on the
        manufacturing sheets) - is costed with the matrix's own UV print rate
        (US$40 per m2, basis unconfirmed) on the board area, beside the
        acrylic, before the multiplier. 2.6.6 left UV print out of the cost
        ("no UI for it yet"), which under-priced these designs. Carried on the
        quote as uv_print_board_area_assumed (the rate's basis - printed area
        or board area - is not confirmed; the board area is the upper bound).
   Nothing else changed: rates, multipliers, add-ons, rounding and every
   2.6.6 rule are byte-for-byte the same code paths.
   ==========================================================================
   (2.6.6 header follows)
   TNF pricing engine 2.6.3                                     2026-09-18
   2.6.3: the US prepaid import tax carried an UNCONFIRMED basis in a comment
   but never said so in the quote. It is now a provisional input on every US
   quote, so it travels into the order record and onto the artwork sheet the
   factory reads. No price changes: the tax is US$21 once per sign, exactly as
   before. What changes is that the order now states it is unconfirmed whether
   that charge is per sign, per carton or per shipment - the single US record
   we hold is one sign in one carton on one shipment and cannot tell them apart.
   2.6.2: large signs are freighted as ONE carton, not one parcel per
   section. Sections are still computed for the factory and the customer, but
   they no longer multiply the freight and never add a power supply. The
   supplied rate card is unchanged to 18.5 kg and is continued above it at the
   card's own final step, so no size is left unpriceable and every price at or
   below 110 cm is exactly what it was. Purchase of large signs is no longer
   gated in the order service.
   2.6.1: reads tnf-geometry 2.5.4 (Rio and Sorority priced as OUTLINE
   constructions: tubing = the glyph contour length; the 8 mm at 150 cm+
   class rule applied to every outline font). Rates and rules unchanged
   from 2.6.0; every other font's price is unchanged.
   2.6.0: AUTOMATIC PRICING THROUGH THE FULL SIZE RANGE (AU to 480 cm in
   1 cm steps, US to 188 in in 1 in steps). The 110 cm threshold is no
   longer a block: it is where the sign is made in SECTIONS. The engine now
   carries a packing model (RATES.packing) that turns the board into
   sections and packages, prices the freight of EVERY package from the
   supplied rate card (never extrapolated beyond its 18.5 kg end), counts
   the power supplies a long run of tubing needs, and applies the
   manufacturer's handling fee once on the whole. Every rule in that model
   that Cindy has not confirmed is labelled PROVISIONAL in the quote
   (quote.provisional, quote.provisionalInputs) - the price is computed and
   shown, never presented as manufacturer-confirmed. Evidence basis, the
   fit against 63 manufacturer quotes and the open questions are in the
   packing block below and in the delivery notes. Retail rules unchanged:
   AU x4.5 / x5, US x2.5 / x3 (outdoor replaces indoor), Premium RGB rule A,
   fixed add-ons once, never per package. Reads tnf-geometry 2.5.3.
   2.5.3: reads tnf-geometry 2.5.3; rates and rules unchanged from 2.5.1.
   2.6.4 (21 Sep 2026): the 12V battery pack can be bought. The blanket block
   was a development placeholder - one boolean refusing every sign, with no
   electrical finding behind it. Every SINGLE-COLOUR sign can now buy the pack,
   with no size limit asserted: an interim 5 m rule was withdrawn because it was
   borrowed from a DIMMER price break and said nothing about a battery. Premium
   RGB / RGBW remain out, as a stated judgement (their controller's draw has
   never been supplied), and the sign still sells without the pack. The 8-12
   hour runtime is NOT in the shop's own supplied guide, which gives no hours at
   all; it is carried as an unverified estimate and every battery quote raises
   battery_runtime_unverified. See RATES.batteryPack.
   2.5.0: 12V battery pack add-on (A$40 / US$40, once per sign, after the
   multiplier, never converted); a quote with the battery was BLOCKED from
   purchase until the supplier confirmed the pack's capacity, load limit and
   which signs it can power (battery_compatibility_unconfirmed) - superseded by
   2.6.4 above. Geometry
   2.5.0 (tube envelope, per-font construction) changes some tubing figures;
   the rates and rules here are otherwise unchanged from 2.4.0.
   --------------------------------------------------------------------------
   Layered so that every component is inspectable and nothing is silently
   included twice:
     1 geometry        (TNF_GEOM - tubing metres, board area, panels)
     2 manufacturer    (USD component costs)
     3 freight         (USD, rate card by volumetric bracket, per package)
     4 handling        (x1.02 Alibaba, applied ONCE to product + freight)
     5 retail          (market multiplier - outdoor REPLACES indoor)
     6 fixed upgrades  (added AFTER the multiplier, once, never converted)
     7 presentation    (rounding, GST component, display strings)

   MONEY. All internal arithmetic is in integer cents of the stated currency.
   One rounding policy: round to whole dollars, once, at step 7.
   ========================================================================== */
(function (root) {
  'use strict';

  var RATES = {
    version: '2.7.2',
    /* the file name and version stay 2.6.9 on purpose: both order services
       load this engine BY NAME and report that version in /health. build
       identifies the sale release without changing what they expect. */
    build: '2.7.0+sale-2026-09-27+uk',
    generated: '2026-09-25',
    source: 'Jack master prompt 2026-09-16, accessory table 2026-09-17, manufacturer matrix, rate card crop (to 18.5 kg), live creator (finish surcharge, 110 cm sections notice), AU/US Cindy quote and paid sheets (large-sign freight, read 17 Sep 2026)',

    /* ---- 5. retail multipliers ---------------------------------------- */
    retail: { US: { indoor: 2.5, outdoor: 3 }, AU: { indoor: 4.5, outdoor: 5 },
              /* 2.7.0: PROVISIONAL until approved in the UK launch review (GBP, VAT included) */
              UK: { indoor: 2.4, outdoor: 2.8 } },
    ukRetailApproved: false,
    ukFreightCardSupplied: false,
    /* 2.7.1: the UK's OWN fixed retail amounts, in pounds, VAT included - Jack's
       preview instruction of 2 Oct 2026 (UK launch review v2, section 2): about
       0.75 x the AU/US number, rounded to a clean price point (nearest GBP1 under
       GBP20, otherwise nearest GBP5). PROVISIONAL: authorised for preparation and
       testing, NOT approved as final live prices (ukFixedApproved false). AU and
       US keep the shared tables below exactly as before and never read this. The
       sale treatment is unchanged and market-blind: the sign, backboard upgrades
       and the RGB premium are discounted; the four accessories are not. */
    ukFixedApproved: false,
    marketFixed: {
      UK: {
        backboard: { cut_to_shape: 0, cut_to_rectangle: 0, cut_to_letter: 30,
                     acrylic_stand: 30, naked_cut: 30, open_box: 30 },
        /* 2.7.2 (Jack, 2 Oct 2026): backboard COLOUR in the UK - clear free, every paid colour GBP40.
           Backboard STYLE upgrades stay GBP30 (above). AU/US untouched. */
        finish: { clear: 0, white: 40, black: 40, silver: 40, gold: 40 },
        wallMountKit: { silver: 0, black: 8, gold: 8 },
        accessories: { hanging_kit: 8, tape_3m: 8, battery_pack: 15 },
        /* Premium RGB: the live AU/US table x 0.75, same rounding; RGBW = 1.5 x the
           UK RGB premium rounded to the nearest GBP5 (AU/US: exactly 1.5 x). */
        premiumRGB: { rgb: {"simple":{"xs":75,"s":75,"m":75,"l":90,"xl":90,"super":115,"xxxl":115,"newsuper":130,"megasuper":150},"fancy":{"xs":75,"s":75,"m":75,"l":90,"xl":90,"super":115,"xxxl":115,"newsuper":130,"megasuper":150},"doubleline":{"xs":100,"s":100,"m":130,"l":190,"xl":190,"super":225,"xxxl":265,"newsuper":300,"megasuper":340}},
                      rgb10: {"simple":{"xs":90,"s":90,"m":90,"l":130,"xl":130,"super":150,"xxxl":150,"newsuper":190,"megasuper":190},"fancy":{"xs":90,"s":90,"m":90,"l":130,"xl":130,"super":150,"xxxl":150,"newsuper":190,"megasuper":190},"doubleline":{"xs":165,"s":165,"m":190,"l":225,"xl":225,"super":265,"xxxl":320,"newsuper":450,"megasuper":565}},
                      rgbwRoundTo: 5 }
      }
    },

    /* ---- 6. fixed retail upgrades, market's own currency, never converted */
    backboard: { cut_to_shape: 0, cut_to_rectangle: 0, cut_to_letter: 40,
                 acrylic_stand: 40, naked_cut: 40, open_box: 40 },
    /* Jack's accessory table, 17 Sep 2026 (supersedes the 16 Sep "$10 for every
       finish"). Local-market amounts: A$ in Australia, US$ in the USA, never
       converted. ONE wall-mount kit with three alternative finishes: choosing a
       finish REPLACES the charge, it never accumulates; silver is free. */
    accessories: { hanging_kit: 10, tape_3m: 10, battery_pack: 20 },
    /* ---- 6b. the 2026 sale (added 2026-09-27, OFF until switched on) -----
       Jack's approved rules:
         Halloween designs            30 % off
         every other catalogue sign   25 % off
         creator "design your own"    25 % off
         accessories                  never discounted, agreed prices kept
       The reduction is applied ONCE, to the sign only, inside the single
       rounding step that already exists - so the discounted price is a real
       price this engine produces, not a number subtracted from a displayed one.

       WHY IT LIVES HERE. This file is the price. The storefront loads it, and
       both order services carry their own copy of it, so putting the rule in
       one place is what makes the page, the server quote, the cart and the
       checkout agree. A promotion applied anywhere else would have to be kept
       in step by hand, and the first thing to drift would be the amount the
       customer is charged.

       IDEMPOTENT BY CONSTRUCTION. Every quote is rebuilt from the manufacturer
       cost upwards, so running it twice gives the same answer. There is no
       stored "already discounted" price to reduce a second time, and re-running
       a price update cannot compound. The same is true of the Shopify variant
       prices this produces: they are written FROM the regular price, never from
       the current one.

       HOW IT IS SWITCHED ON. enabled:false ships in every copy, so installing
       this file changes no price anywhere. Three ways it turns on, in order:
         1. enabled:true in this table            - the real switch, both services
         2. TNF_PROMOTION_OVERRIDE on the page    - set ONLY by
            snippets/tnf-promo-config.liquid, which emits nothing on the live
            theme, so an unpublished preview can be reviewed end to end while
            the live storefront and the deployed services stay at full price
         3. startsAt / endsAt                     - optional window, UTC
       Turning it off again is the same switch; nothing else has to be undone. */
    promotion: {
      enabled: false,
      id: 'tnf-sale-2026-10',
      startsAt: null,
      endsAt: null,
      rates: { halloween: 0.30, standard: 0.25 },
      /* The 17 designs in the Halloween collection, 27 Sep 2026. A HANDLE list,
         not a collection lookup: the storefront and both services must reach the
         same answer without asking Shopify, or the page and the charge could
         disagree while a lookup is stale. Add a handle here to move a design to
         30 %; anything not listed - including every creator design, which sends
         no handle at all - takes the standard rate. */
      halloweenHandles: ['alien-led-neon-sign', 'angry-pumpkin', 'balaclava-ghost', 'bed-sheet-ghost',
        'boo-ghost-led-neon-sign', 'booo', 'booo-1', 'ghost-1', 'halloween', 'hell-here-hello-there',
        'scream', 'simple-bat', 'simple-ghost-1', 'simple-pumpkin', 'simple-spider',
        'trick-or-treat-1', 'trick-or-treat-2'],
      /* never discounted - the same four keys the catalogue page already treats
         as accessories, so page and engine cannot disagree about what a sign is */
      excludeKeys: { wall_mounting_kit: 1, hanging_kit: 1, tape_3m: 1, battery_pack: 1 },
      basis: 'Jack, 27 Sep 2026. Applied once to the sign; accessories excluded; one rounding, unchanged.'
    },
    /* ---- 12V battery pack (2.6.4) --------------------------------------
       A$20 / US$20, once per sign, after the multiplier, never converted.
       (Jack, 27 Sep 2026: reduced from 40. Same number in each market's own
       currency. The promotion below NEVER discounts it - it is an accessory,
       and accessories keep their agreed price.)

       WHY IT USED TO REQUIRE A MANUAL QUOTE, traced 21 Sep 2026: a development
       placeholder, not an electrical finding. One boolean,
       compatibilityConfirmed: false, raised battery_compatibility_unconfirmed
       for EVERY sign - any size, any colour mode - and the note beside it said
       only that the supplier sheet had never been provided. No measurement, no
       manufacturer statement and no fault about the pack appears anywhere in
       this project. The blanket block is gone.

       WHAT THE SUPPLIED GUIDE ACTUALLY SAYS. Read 21 Sep 2026 from
       theme/assets/tnf-dimmer-remote-battery-guide.pdf (md5
       f91774dbdb3094157ac0c0b65d8fd141), the shop's own Option 9 sheet:
         "8 x AA BATTERIES REQUIRED"
         "A portable power option for compatible signs, ideal when a wall
          socket is out of reach."
         "Plug the pack's attached cable into the power lead of your
          compatible sign."
         "Battery life varies with sign size, brightness and batteries.
          Ask us to confirm compatibility."
       So the guide confirms the 8 AA cells and confirms that compatibility is
       a real, per-sign question. It gives NO capacity, NO current draw, NO
       length limit and - importantly - NO RUNTIME. It deliberately declines to
       state hours.

       THE SIZE LIMIT HAS BEEN REMOVED. An earlier version of this block gated
       the battery at 5 m of tubing, borrowed from the manufacturer matrix's
       small/large DIMMER price break. That break is about which dimmer part is
       fitted; it says nothing about what a battery can run, and presenting it
       as a battery limit was wrong. Nothing in the records supports any
       numeric limit, so none is asserted. Every single-colour sign can buy the
       pack, which is what the shop sells it for.

       WHAT IS STILL GATED, and why it is a judgement rather than a measurement:
       Premium RGB and RGBW. The manufacturer matrix prices their controller as
       a separate part and gives no load figure for it, and the guide's
       compatibility line gives no basis for including it. Offering the pack
       there would be assuming something nobody has stated. batteryOnSingleColourOnly
       is one constant - flip it the day the manufacturer confirms the
       controller's draw.

       THE 8-12 HOUR RUNTIME IS NOT IN THE GUIDE. It traces to one line from
       Jack on 17 Sep 2026. The shop's own sheet says life "varies with sign
       size, brightness and batteries" and gives no hours, so the figure shown
       to customers is BROADER than anything the supplied material states. It is
       carried here as an unverified estimate, every battery quote raises
       battery_runtime_unverified, and the customer-facing wording says
       "Estimated". An order-of-magnitude check does not help it either: eight AA
       alkaline cells hold roughly 24-34 Wh, so 8-12 hours implies about 2-3 W
       for the whole sign, and LED neon flex is generally far thirstier per
       metre. That uses a GENERAL figure for flex, not a supplied one, so it is
       a reason to ask the manufacturer - not a measurement.
       TO CLOSE THIS PROPERLY, two numbers are needed from the manufacturer: the
       pack's capacity (mAh or Wh) and the flex's draw per metre. With those the
       runtime becomes a calculation per sign and a real compatibility limit can
       replace the judgement above. */
    batteryPack: { customerCharge: 20,
                   copy: '12V Battery Pack - power your sign without a power point. Requires 8 AA batteries. Estimated runtime: 8-12 hours, depending on your sign\'s size and power consumption.',
                   cells: '8 x AA',
                   cellsSource: 'tnf-dimmer-remote-battery-guide.pdf, read 21 Sep 2026 - SUPPLIED',
                   batteryOnSingleColourOnly: true,
                   singleColourBasis: 'JUDGEMENT, not a measurement: the manufacturer matrix prices the Premium RGB / RGBW controller as a separate part and gives no load figure, and the supplied guide gives no basis for including it. Flip this the day that draw is confirmed.',
                   maxTubing_m: null,
                   maxTubingBasis: 'REMOVED 21 Sep 2026. A 5 m limit was previously asserted from the matrix\'s small/large DIMMER price break, which is about which dimmer part is fitted and says nothing about a battery. Nothing in the records supports any numeric limit, so none is asserted.',
                   runtimeClaim: '8-12 hours',
                   runtimeVerified: false,
                   runtimeSource: 'Jack, 17 Sep 2026. NOT in the supplied guide, which states only that battery life "varies with sign size, brightness and batteries" and gives no hours.',
                   runtimeCheck: 'Eight AA alkaline cells hold roughly 24-34 Wh, so 8-12 hours implies about 2-3 W for the whole sign. Typical LED neon flex draws far more per metre. General figure, not supplied - ask the manufacturer for the pack capacity and the flex W/m.',
                   compatibilityLine: 'Battery life varies with sign size, brightness and batteries. Ask us to confirm compatibility. (supplied guide, 21 Sep 2026)',
                   compatibilityConfirmed: false },
    wallMountKit: { silver: 0, black: 10, gold: 10 },
    wallMountFinishes: ['silver', 'gold', 'black'],
    /* Free Dimmer & Remote: no customer charge (the live creator's checkbox,
       ticked by default). The dimmer / RGB controller COST is already inside
       C_USD via the manufacturer matrix, once, so nothing is added here. */
    freeDimmer: { customerCharge: 0, costInsideC_USD: true },
    /* Backboard COLOUR: the live creator charges +$40 for White, Black, Silver
       and Gold acrylic (data-price on the colour tiles; boardcolprice in
       getNewPrice). Preserved because it was not asked to change. */
    finish: { clear: 0, white: 40, black: 40, silver: 40, gold: 40 },
    supersededCharges: [
      { what: 'Hanging kit', was: 25, now: 10, on: '2026-09-16' },
      { what: '3M tape', was: 20, now: 10, on: '2026-09-16' },
      { what: 'Wall mount Gold', was: 25, now: 10, on: '2026-09-17' },
      { what: 'Wall mount Black', was: 25, now: 10, on: '2026-09-17' },
      { what: 'Wall mount Silver', was: 0, now: 0, on: '2026-09-17', note: 'stays free (16 Sep draft had $10)' }
    ],

    /* ---- 2. manufacturer matrix, USD ---------------------------------- */
    mfr: {
      tubing:   { indoor: 13, outdoor: 16, rgbIndoor: 16, rgbOutdoor: 19 },
      dimmer:   { smallUnder5m: 3, largeOver5m: 12, outdoorWaterproof: 12,
                  exactly5m: null },              // UNCONFIRMED
      rgbController: { indoor: 10, outdoorWaterproof: 15 },
      power:    { indoorSAA: 5, outdoorUnder6m: 14.50, outdoor6to12m: 20.30,
                  outdoorOver12m: null },          // manufacturer must calculate
      uvPrint_per_m2: 40,                          // basis UNCONFIRMED
      acrylic_per_m2: 67.42,                       // FITTED, not supplied
      acrylicConfirmed: false,
      usPrepaidTax_per_piece: 21,               // "usually"; large-sign rule UNCONFIRMED
      usPrepaidTaxConfirmed: false,
      handlingFactor: 1.02,
      handlingAppliedTo: 'product + freight',
      minimumCharge: null                          // UNCONFIRMED
    },

    /* ---- 3. freight ---------------------------------------------------- */
    freight: {
      units: 'USD', bracketKg: 0.5, firstKg: 0.5, lastKg: 18.5,
      volumetric: 'ceil(((W_cm+10)*(H_cm+10)*8)/5000) per package',
      roundingConfirmed: false,
      dhlOver25kg: 23, dhlBasisConfirmed: false,
      AU: [27,33,39,44,50,56,61,67,73,78,84,90,95,101,107,112,118,124,129,135,141,
           146,152,158,163,169,175,180,186,192,197,203,209,214,220,226,231],
      US: [28,32,36,40,44,49,54,59,64,69,73,78,83,88,93,98,103,108,113,118,123,
           128,133,138,143,148,153,158,163,168,172,177,182,187,192,197,202],
      /* 2.7.0: the UK column has NOT been supplied. The US column stands in, PROVISIONAL
         (uk_freight_card_unconfirmed on every UK quote). Replace with the card's UK figures. */
      UK: [28,32,36,40,44,49,54,59,64,69,73,78,83,88,93,98,103,108,113,118,123,
           128,133,138,143,148,153,158,163,168,172,177,182,187,192,197,202]
    },

    /* ---- 3b. packing and freight above 110 cm (2.6.2) -------------------
       2.6.1 and earlier packed EVERY section in its own carton and charged
       the rate card once per carton. That is wrong: a section of acrylic is
       not automatically a separate parcel. Against the 61 single-sign
       manufacturer records (110-372 cm, read 17 Sep 2026) that rule came out
       at 0.85 x the actual freight at the median and outside +/-25 % for 27
       of the 61, because real freight FLATTENS with size (262 cm naked cut
       US$196; 300 cm cut to shape US$414 twice) while a per-section charge
       keeps climbing.

       WHAT IS CONFIRMED
       - The store's own rule: above 110 cm on an axis the sign is made in
         parts. Jack, 13 Aug 2024, to a customer: the largest piece the
         machine cuts is 120 cm; large signs are "in 2 parts", cut to shape,
         assembled at mounting, the join invisible once mounted; shipping
         such a sign whole was advised against as damage-prone and dearer
         (15 Aug 2024).
       - The manufacturer's volumetric formula (W+10)(H+10)xD/5000 and the
         freight card to 18.5 kg, both supplied.
       - A multi-section sign travels under ONE tracking number with ONE
         freight charge in the paid records.

       WHAT IS ASSUMED OR EXTRAPOLATED (neither is a manufacturer figure)
       - ASSUMPTION: the sections stack into ONE carton, and that carton is
         8 cm deep PER SECTION. The 8 cm comes from the supplied volumetric
         formula; "per section" is ours. The carton's billable volume is NOT
         conserved when the sign is cut - the +10 cm packing allowance applies
         to each stacked layer, so a cut sign is billed a few per cent heavier
         than the same sign uncut. That is deliberate (a stack of panels
         carries more packaging than one flat panel) and it is what keeps the
         price from falling as a sign crosses 110 cm.
       - EXTRAPOLATION: above 18.5 kg the card is continued at its own final
         increment (US$10.00/kg - its last two rows differ by US$5 per 0.5 kg
         in both markets). No rate above the card has ever been supplied. This
         is not a manufacturer rate and must not be described as one.
       CHECKED against all 61 usable manufacturer records. Read that word
       carefully: no NUMBER here was fitted to those records, but the model's
       SHAPE was chosen after reading them - the one-carton rule replaced a
       per-section rule precisely because the records show freight flattening
       as signs grow. So this is a consistency check against the evidence the
       design came from, NOT an independent out-of-sample validation, and it
       must not be described as one.
       Result: median model/actual 0.87, 28 of the 61 outside +/-25 %, worst
       underquote 0.41 x, worst overquote 2.35 x. On the card the median is
       0.82 and on the extrapolation 1.17.
       The split that matters most: where the record STATES a height (n=9) the
       median is 0.94 and only 2 are outside +/-25 %; where the height is our
       0.3 x width assumption (n=52) the median is 0.86 and 26 are outside. The
       scatter is mostly our missing heights, not the freight rule - but n=9 is
       too small to lean on.
       ALL 61 records are AU-destined. The US column of the card is tested by
       nothing at all. The
       full record-by-record table is in evidence/13-freight-validation. The
       spread
         is wide because the same nominal size ships at very different prices
         (150 cm cut to shape: US$189 to US$465) - box depth and construction
         drive it - and most records give no height, so 0.3 x width was
         assumed when fitting. The creator knows the real height, so its
         inputs are better than that spread suggests.

       WHAT IS STILL UNKNOWN (question 1 for Cindy)
       - Whether a sectioned sign is ever split across two cartons, and the
         real packed depth. If very large signs do go in two cartons, the
         freight below is understated for those sizes.
       SECTIONS ARE STILL COMPUTED - the factory needs the cut plan and the
       customer needs to know the sign arrives in parts - but they no longer
       multiply the freight, and they never add a power supply. */
    packing: {
      basis: "ASSUMED one carton per sign with sections stacked; SUPPLIED card to 18.5 kg; EXTRAPOLATED above it at the card's own final increment. Checked against 61 AU manufacturer records (the same records the rule's shape was chosen from, so not an independent test): median 0.87 x actual, 28 outside +/-25 %.",
      confirmed: false,
      maxOnePiece_mm: 1100,
      maxOnePieceSource: 'live creator notice (over 110 cm the sign is made in parts); Jack 13 Aug 2024 gives 120 cm as the machine limit',
      rule: 'one_carton_sections_stacked',
      cartonDepth_cm: 8,
      cartonDepthBasis: 'the 8 cm is SUPPLIED (it is in the manufacturer volumetric formula); depth = 8 cm x sections is an ASSUMPTION about how sections stack, not a packing list. No price at or below 110 cm changes, because a one-section carton is the supplied formula unaltered.',
      maxSectionsAcross: 8, maxSectionsDown: 4,
      aboveCardBasis: "EXTRAPOLATION, not a supplied rate: the card's own final increment per kilogram (US$10.00/kg in both markets), continued above 18.5 kg. No rate above the card has ever been given by the manufacturer.",
      validation: { fittedParameters: 0, note: 'No NUMBER was fitted to these records, but the rule\'s SHAPE (one carton, sections stacked) was chosen after reading them, so this is a consistency check, not an independent out-of-sample validation.', records: 61, excluded: 12, medianModelOverActual: 0.87, outside25pct: 28, worstUnder: 0.41, worstOver: 2.35, onCard: { n: 43, median: 0.82 }, extrapolated: { n: 18, median: 1.17 }, usRecords: 0, usColumnTested: false, heightGiven: { n: 9, median: 0.94, outside25pct: 2 }, heightAssumed: { n: 52, median: 0.86, outside25pct: 26 }, largestRecord_cm: 372, sellTo_cm: 480, evidence: 'evidence/13-freight-validation/validation.txt', heightAssumption: '0.3 x width where the record gives no height (48 of the 61 records give none)', read: '2026-09-17', validated: '2026-09-18' },
      extraCostsPerSection: null           // joins, wiring, extra feeds: not charged, UNCONFIRMED
    },
    /* ---- 2b. power supplies for long tubing (2.6.0) --------------------
       The matrix gives ONE indoor SAA supply (US$5) and outdoor supplies by
       tubing length (<6 m 14.50, 6-12 m 20.30, above 12 m "must be
       recalculated by the manufacturer"). The outdoor tiers imply a supply
       covers about 12 m of tube. Indoor signs above 12 m are therefore given
       one extra US$5 supply per further 12 m - an ASSUMPTION, flagged on the
       quote; outdoor above 12 m stays a manufacturer calculation (blocked). */
    power: { indoorSupplyMaxMetres: 12, indoorSupplyBasis: 'ASSUMED from the outdoor tier structure', confirmed: false },

    /* ---- 2c. opt-in hardware policies (2.6.7) --------------------------
       Applied ONLY to a geometry that names the policy (g.hardwarePolicy).
       See the 2.6.7 header. None of these is a manufacturer figure. */
    hardwarePolicies: {
      'catalogue-1': {
        dimmerAtExactly5m: 'large',
        dimmerAtExactly5mBasis: 'the matrix gives a small dimmer under 5 m and a large dimmer over 5 m; exactly 5 m fits neither, so the part rated for the longer run is fitted',
        outdoorOver12m: 'equal_runs_each_with_supplied_supply',
        outdoorRunMaxMetres: 12,
        outdoorOver12mBasis: 'ASSUMED: the fewest equal runs of at most 12 m, each with the supplied outdoor supply for its length; one waterproof dimmer, as the indoor rule keeps one dimmer across several supplies',
        sectionsUncapped: true,
        sectionsBasis: 'every section inside the 110 cm one-piece limit: ceil(W / 110) across, ceil(H / 110) down',
        uvPrint: 'supplied_rate_on_board_area',
        uvPrintBasis: 'ASSUMED: the matrix UV print rate (uvPrint_per_m2, basis unconfirmed) on the board area of a design whose artwork is printed (geometry uvPrint)',
        confirmed: false
      },
      'catalogue-2': {
        dimmerAtExactly5m: 'large',
        dimmerAtExactly5mBasis: 'the matrix gives a small dimmer under 5 m and a large dimmer over 5 m; exactly 5 m fits neither, so the part rated for the longer run is fitted',
        outdoorOver12m: 'equal_runs_each_with_supplied_supply',
        outdoorRunMaxMetres: 12,
        outdoorOver12mBasis: 'ASSUMED: the fewest equal runs of at most 12 m, each with the supplied outdoor supply for its length; one waterproof dimmer, as the indoor rule keeps one dimmer across several supplies',
        sectionsFromGeometry: true,
        sectionsBasis: 'the cut plan of the catalogue geometry adapter 1.2.0 (g.parts): one piece within 110 cm; two pieces when the longest side is under 150 cm and each half is within the 120 cm machine limit (PROVISIONAL); otherwise the creator grid ceil(W/110) x ceil(H/110)',
        uvPrint: 'supplied_rate_on_board_area',
        uvPrintBasis: 'ASSUMED: the matrix UV print rate (uvPrint_per_m2, basis unconfirmed) on the board area of a design whose artwork is printed (geometry uvPrint)',
        confirmed: false
      }
    },

    /* ---- Premium RGB / RGBW: the LIVE site's rule, carried forward unchanged ----
       Read from neon.js on the live theme (rgbprice / rgbprice10 / rgbandwater /
       rgbandwater10). AUD add-on keyed by font class and the legacy size card
       nearest the design's letter height; a higher table when the text has 10+
       characters; RGBW = RGB x 1.5. NOT a new rule. Outdoor + Premium RGB is
       BLOCKED pending Jack's decision: the old combined table is stored here for
       reference but the new outdoor multiplier would otherwise double-count. */
    premiumRGB: {
      source: 'live neon.js tables, read 2026-09-17', preserved: true, currencyNote: 'AUD figures on the live site; the US site showed the same numbers because of the FX bug',
      rgb: {"simple": {"xs": 100, "s": 100, "m": 100, "l": 120, "xl": 120, "super": 150, "xxxl": 150, "newsuper": 170, "megasuper": 200}, "fancy": {"xs": 100, "s": 100, "m": 100, "l": 120, "xl": 120, "super": 150, "xxxl": 150, "newsuper": 170, "megasuper": 200}, "doubleline": {"xs": 130, "s": 130, "m": 170, "l": 250, "xl": 250, "super": 300, "xxxl": 350, "newsuper": 400, "megasuper": 450}},
      rgb10: {"simple": {"xs": 120, "s": 120, "m": 120, "l": 170, "xl": 170, "super": 200, "xxxl": 200, "newsuper": 250, "megasuper": 250}, "fancy": {"xs": 120, "s": 120, "m": 120, "l": 170, "xl": 170, "super": 200, "xxxl": 200, "newsuper": 250, "megasuper": 250}, "doubleline": {"xs": 220, "s": 220, "m": 250, "l": 300, "xl": 300, "super": 350, "xxxl": 425, "newsuper": 600, "megasuper": 750}},
      rgbOutdoorCombined: {"simple": {"xs": 160, "s": 160, "m": 160, "l": 192, "xl": 192, "super": 220, "xxxl": 240, "newsuper": 272, "megasuper": 320}, "fancy": {"xs": 160, "s": 160, "m": 160, "l": 192, "xl": 192, "super": 220, "xxxl": 240, "newsuper": 272, "megasuper": 320}, "doubleline": {"xs": 208, "s": 208, "m": 272, "l": 400, "xl": 400, "super": 500, "xxxl": 570, "newsuper": 640, "megasuper": 800}},
      rgbOutdoorCombined10: {"simple": {"xs": 192, "s": 192, "m": 192, "l": 272, "xl": 272, "super": 400}, "fancy": {"xs": 192, "s": 192, "m": 192, "l": 272, "xl": 272, "super": 400}, "doubleline": {"xs": 352, "s": 352, "m": 400, "l": 480, "xl": 480, "super": 700, "xxxl": 800, "newsuper": 960, "megasuper": 1200}},
      rgbwFactor: 1.5, longTextChars: 10,
      sizeKeys: { simple:[['xs',8],['s',9],['m',12],['l',15],['xl',20],['super',24],['xxxl',27],['newsuper',33],['megasuper',40]],
                  fancy:[['xs',10],['s',12],['m',16],['l',20],['xl',27],['super',30],['xxxl',36],['newsuper',40],['megasuper',45]],
                  doubleline:[['m',20],['l',26],['xl',34],['super',40],['xxxl',46],['newsuper',50],['megasuper',55]] },
      liveBug: 'rgbandwater10 has no simple/fancy entry above super: the live site prices those as NaN',
      /* HOW the add-on combines with the cost model is NOT decided. The live
         site added the table to a size-card price that did not vary by colour
         mode. The manufacturer matrix gives RGB tubing its own per-metre rate
         and a controller. Three candidate rules are computed on every RGB
         quote (protectedRGB.candidates) so they can be compared; the one
         named here is what the total shows, and every RGB quote carries the
         flag premium_rgb_rule_provisional until Jack approves a rule. */
      rule: {
        /* A is the approved pricing: the live add-on table on the single-colour
           price. Jack, 17 Sep 2026: "Do not change my approved Premium
           RGB/RGBW pricing without agreement." B and C stay as comparisons. */
        selected: 'A', approved: true, costBasisConfirmed: false,
        candidates: {
          A: 'single-colour cost base (indoor rate + dimmer) x multiplier + live add-on  [the live rule, literally]',
          B: 'RGB cost base (RGB rate + controller) x multiplier + live add-on            [current engine]',
          C: 'RGB cost base (RGB rate + controller) x multiplier, no add-on               [cost-only]'
        }
      }
    },
    tax: { AU: { gstInclusive: true, rate: 0.10 }, US: { salesTax: 'not collected in creator' } },

    /* What C_USD includes and excludes. Nothing here is charged twice. */
    inclusions: {
      inC_USD: ['tubing @ per-metre rate', 'dimmer or RGB controller', 'power supply',
                'acrylic backing on the board area (all six styles)', 'freight per package',
                'US prepaid import tax (US market only)', 'x1.02 handling on all of the above, once'],
      notInC_USD: ['UV print (no UI for it yet)', 'stand or box structure beyond the acrylic area',
                   'coloured finish surcharge (none supplied)', 'any minimum charge (none supplied)',
                   'DHL >25 kg surcharge (no package reaches it under the 18.5 kg card)'],
      retailOnly: ['backboard style upgrade $40 (4 styles)', 'backboard colour $40 (4 colours, live rule)', 'wall mounting kit: silver free, black $10, gold $10', 'hanging kit $10', '3M tape $10', 'free dimmer & remote $0', '12V battery pack $40 (single colour; no size limit asserted; runtime UNVERIFIED - not in the supplied guide)']
    }
  };

  /* ------------------------------------------------------------------ */
  function c(n) { return Math.round(n * 100); }       // dollars -> cents
  function d(n) { return n / 100; }                   // cents -> dollars

  /* the manufacturer's supplied volumetric formula, unchanged. The 8 cm is
     part of that formula (one carton, one flat sign). A sectioned sign stacks
     its panels, so the carton is 8 cm per section - which keeps the carton's
     VOLUME the same however the sign is cut, and leaves every price at or
     below 110 cm exactly as approved. */
  function volumetricKg(w_cm, h_cm, depth_cm) {
    return ((w_cm + 10) * (h_cm + 10) * (depth_cm || RATES.packing.cartonDepth_cm)) / 5000;
  }

  /* The supplied card is exact to 18.5 kg. Above it the card is CONTINUED at
     its own final step - the card's last two rows differ by US$5 per 0.5 kg in
     both markets, so the rate per extra kilogram is read from the card itself
     rather than invented (2.6.2). Before 2.6.2 a carton past the end of the
     card was simply blocked, which left every sign over about 150 cm
     unpriceable. Against the 61 manufacturer records this continuation sits at
     1.06 x the actual freight at the median, while the card below 18.5 kg sits
     at 0.72 x - so a large sign is priced on a basis no thinner than the sizes
     already being sold. Quotes that use it are marked. */
  function cardSlopePerKg(market) {
    var F = RATES.freight, n = Math.round(F.lastKg / F.bracketKg) - 1;
    return (F[market][n] - F[market][n - 1]) / F.bracketKg;
  }
  function freightUSD(market, kg) {
    var F = RATES.freight;
    var b = Math.max(F.firstKg, Math.ceil(kg / F.bracketKg) * F.bracketKg);
    if (b > F.lastKg) {
      var last = F[market][Math.round(F.lastKg / F.bracketKg) - 1], rate = cardSlopePerKg(market);
      var usd = Math.round((last + (kg - F.lastKg) * rate) * 100) / 100;
      return { usd: usd, blocked: false, bracketKg: null, kg: kg, extended: true, ratePerKg: rate,
               basis: 'the supplied card to ' + F.lastKg + ' kg, continued above it at the card\'s own final step (US$' + rate.toFixed(2) + '/kg)' };
    }
    var i = Math.round(b / F.bracketKg) - 1;
    return { usd: F[market][i], blocked: false, bracketKg: b, kg: kg, extended: false, basis: 'supplied rate card' };
  }

  /* ---- 3b. carton plan: one carton per sign, sections stacked (2.6.2) ----
     Returns the cut plan the factory needs (nx across, ny down) and the ONE
     carton the sign ships in: its depth grows with the number of sections,
     its length is the longest section, its height the tallest. Freight is the
     card for that carton's volumetric weight, extended above the card at the
     fitted rate. Sections never multiply the freight. */
  function cartonPlan(market, w_mm, h_mm, uncapped, planned) {
    var K = RATES.packing, lim = K.maxOnePiece_mm;
    var W = w_mm / 10, H = h_mm / 10;
    /* 2.6.7: uncapped (catalogue-1 policy) - every section inside the limit */
    var capX = uncapped ? Infinity : K.maxSectionsAcross, capY = uncapped ? Infinity : K.maxSectionsDown;
    var nx = w_mm > lim ? Math.min(capX, Math.ceil(w_mm / lim)) : 1;
    var ny = h_mm > lim ? Math.min(capY, Math.ceil(h_mm / lim)) : 1;
    /* 2.6.8 catalogue-2: the geometry adapter's own plan (one plan for notice, freight, cart and factory) */
    if (planned && planned.across >= 1 && planned.down >= 1) { nx = planned.across; ny = planned.down; }
    var sections = nx * ny, divided = sections > 1;
    var sw = W / nx, sh = H / ny;
    /* The carton holds the stacked sections: 8 cm of depth per section.
       Volume is NOT conserved when the sign is cut, and should not be claimed
       to be. Billable volume for nx x ny sections is
         (W/nx + 10)(H/ny + 10) x 8 x nx x ny
       = 8 x [ W.H + 10.W.ny + 10.H.nx + 100.nx.ny ]
       against 8 x [ W.H + 10W + 10H + 100 ] uncut - larger by
         8 x [ 10.W.(ny-1) + 10.H.(nx-1) + 100.(nx.ny - 1) ],
       which is zero for one section and positive for more. The extra is the
       +10 cm packing allowance counted once per stacked layer. So cutting a
       sign always makes its billed freight a little DEARER, never cheaper,
       and the price is monotone across the 110 cm boundary. */
    var depth = K.cartonDepth_cm * sections;
    var kg = volumetricKg(sw, sh, depth);
    var f = freightUSD(market, kg);
    return {
      rule: K.rule, divided: divided, sections: sections, nx: nx, ny: ny,
      section_cm: [Math.round(sw * 10) / 10, Math.round(sh * 10) / 10],
      carton_cm: [Math.round(sw * 10) / 10, Math.round(sh * 10) / 10, Math.round(depth * 10) / 10],
      packages: 1, kg: Math.round(kg * 100) / 100, freight: f,
      usd: f.usd, blocked: false, extended: !!f.extended, provisional: true
    };
  }

  /* ---- 2 + 3: manufacturer cost -------------------------------------- */
  /* opts.rgbCost === false costs an RGB design as if it were single colour
     (candidate rule A); the default costs RGB tubing and a controller. */
  function manufacturer(g, cfg, opts) {
    var lines = [], m = RATES.mfr;
    var L = g.tubing.L_m;
    var outdoor = !!cfg.outdoor;
    var rgb = (cfg.colourMode === 'rgb' || cfg.colourMode === 'rgbw') && !(opts && opts.rgbCost === false);
    var cents = 0, unresolved = [];
    /* 2.6.7: an opt-in hardware policy named by the geometry (catalogue-1), else none */
    var POL = (g.hardwarePolicy && RATES.hardwarePolicies[g.hardwarePolicy]) || null;
    var provisional = [];

    var rate = rgb ? (outdoor ? m.tubing.rgbOutdoor : m.tubing.rgbIndoor)
                   : (outdoor ? m.tubing.outdoor : m.tubing.indoor);
    var tub = c(L * rate);
    lines.push({ k: 'tubing', label: L.toFixed(2) + ' m @ US$' + rate + '/m', usd: d(tub) });
    cents += tub;

    if (rgb) {
      var ctl = c(outdoor ? m.rgbController.outdoorWaterproof : m.rgbController.indoor);
      lines.push({ k: 'controller', label: 'RGB controller set', usd: d(ctl) }); cents += ctl;
    } else if (outdoor) {
      var wd = c(m.dimmer.outdoorWaterproof);
      lines.push({ k: 'dimmer', label: 'waterproof dimmer', usd: d(wd) }); cents += wd;
    } else {
      if (Math.abs(L - 5) < 1e-9 && POL && POL.dimmerAtExactly5m === 'large') {
        /* 2.6.7 catalogue-1: the large dimmer, flagged */
        var dmx = c(m.dimmer.largeOver5m);
        lines.push({ k: 'dimmer', label: 'large dimmer (tube exactly 5 m - large fitted, ASSUMED)', usd: d(dmx) });
        cents += dmx; provisional.push('dimmer_at_exactly_5m_large_fitted');
      } else if (Math.abs(L - 5) < 1e-9) {
        unresolved.push('dimmer_at_exactly_5m');
        lines.push({ k: 'dimmer', label: 'dimmer at exactly 5 m - UNCONFIRMED', usd: null });
      } else {
        var dm = c(L < 5 ? m.dimmer.smallUnder5m : m.dimmer.largeOver5m);
        lines.push({ k: 'dimmer', label: L < 5 ? 'small dimmer (<5 m)' : 'large dimmer (>5 m)', usd: d(dm) });
        cents += dm;
      }
    }

    var supplies = 1;
    if (outdoor && L > 12 && POL && POL.outdoorOver12m) {
      /* 2.6.7 catalogue-1: the fewest equal runs of at most 12 m, each with its supplied supply */
      var runMax = POL.outdoorRunMaxMetres;
      supplies = Math.ceil(L / runMax - 1e-9);
      var runL = L / supplies;
      var each = c(runL < 6 ? m.power.outdoorUnder6m : m.power.outdoor6to12m);
      var pws = each * supplies;
      lines.push({ k: 'power', label: supplies + ' x outdoor power supply (' + supplies + ' runs of ' + runL.toFixed(2) + ' m, at most ' + runMax + ' m each - ASSUMED)', usd: d(pws), count: supplies });
      cents += pws; provisional.push('outdoor_power_supplies_over_12m_assumed');
    } else if (outdoor) {
      if (L > 12) { unresolved.push('outdoor_power_over_12m');
        lines.push({ k: 'power', label: 'outdoor power >12 m - manufacturer must calculate', usd: null }); }
      else { var pw = c(L < 6 ? m.power.outdoorUnder6m : m.power.outdoor6to12m);
        lines.push({ k: 'power', label: 'outdoor power supply', usd: d(pw) }); cents += pw; }
    } else {
      /* 2.6.0: one indoor supply per 12 m of tubing (ASSUMED, see RATES.power) */
      supplies = Math.max(1, Math.ceil(L / RATES.power.indoorSupplyMaxMetres - 1e-9));
      var ps = c(m.power.indoorSAA) * supplies;
      lines.push({ k: 'power', label: supplies === 1 ? 'indoor SAA power supply' : supplies + ' x indoor SAA power supply (one per ' + RATES.power.indoorSupplyMaxMetres + ' m of tube - ASSUMED)', usd: d(ps), count: supplies });
      cents += ps;
      if (supplies > 1) provisional.push('indoor_power_supplies_over_12m_assumed');
    }

    /* Acrylic backing. Charged for every style on that style's own board
       area - a naked cut still has acrylic behind the tube, just less of it
       (5 mm margin instead of 15-25). The rate is FITTED to three invoices
       and is not a supplied manufacturer figure. */
    var area = (g.board.w_mm / 1000) * (g.board.h_mm / 1000);
    var ac = c(area * m.acrylic_per_m2);
    lines.push({ k: 'acrylic', label: 'acrylic ' + area.toFixed(3) + ' m2 @ US$' + m.acrylic_per_m2 +
                 '/m2 (' + g.board.style.replace(/_/g, ' ') + ') - rate FITTED, unconfirmed', usd: d(ac) });
    cents += ac;
    /* 2.6.7 catalogue-1: UV-printed artwork - the matrix UV print rate on the board area (basis unconfirmed) */
    if (POL && POL.uvPrint && g.uvPrint) {
      var uvc = c(area * m.uvPrint_per_m2);
      lines.push({ k: 'uv_print', label: 'UV print ' + area.toFixed(3) + ' m2 @ US$' + m.uvPrint_per_m2 + '/m2 on the board area - basis UNCONFIRMED (catalogue-1)', usd: d(uvc) });
      cents += uvc; provisional.push('uv_print_board_area_assumed');
    }

    /* 2.6.2: ONE carton per sign. Over 110 cm on an axis the sign is made in
       sections - the factory needs that cut plan and the customer is told the
       sign arrives in parts - but the sections stack into one carton and are
       freighted once. The card is exact to 18.5 kg and extended above it at
       the fitted rate, so no size is left unpriceable. */
    /* 2.7.0: the market for freight and import tax is the QUOTE's market (g.market is the size grid:
       AU or US; a UK sign is measured on the AU grid). For AU and US the two are always equal. */
    var MKT = cfg.market === 'US' ? 'US' : cfg.market === 'UK' ? 'UK' : 'AU';
    if (MKT === 'UK' && !RATES.ukFreightCardSupplied) provisional.push('uk_freight_card_unconfirmed');
    var plan = cartonPlan(MKT, g.board.w_mm, g.board.h_mm, !!(POL && POL.sectionsUncapped),
                          (POL && POL.sectionsFromGeometry && g.parts) ? g.parts : null);
    var fkg = plan.kg, fcents = c(plan.usd), fblocked = false;
    if (plan.divided) provisional.push('oversize_sections_packing_provisional');
    if (plan.extended) provisional.push('freight_above_rate_card_fitted');
    lines.push({ k: 'freight', label: (plan.divided
      ? plan.sections + ' section' + (plan.sections === 1 ? '' : 's') + ' (' + plan.nx + ' across x ' + plan.ny + ' down, ' + plan.section_cm[0] + ' x ' + plan.section_cm[1] + ' cm) stacked in ONE carton '
      : '1 carton ') + plan.carton_cm.join(' x ') + ' cm @ ' + plan.kg + ' kg = US$' + plan.usd +
      ' - ' + plan.freight.basis, usd: plan.usd });

    /* US prepaid import tax. Cindy's US sheet folds this into the shipping
       figure ("65 tax included"), so it belongs with freight, before handling.
       US$21 is the "usual" figure; the large-sign / multi-package rule is not known. */
    var taxcents = 0;
    if (MKT === 'US') {
      taxcents = c(m.usPrepaidTax_per_piece);
      lines.push({ k: 'us_prepaid_tax', label: 'US prepaid import tax, US$' + m.usPrepaidTax_per_piece +
                   ' once per sign - basis UNCONFIRMED', usd: d(taxcents) });
      /* 2.6.3: the basis was documented but never surfaced. The only evidence is ONE paid US
         record where a US$148 DHL charge was written down as "127 + 21" - one sign, one carton,
         one shipment - so it cannot tell per-sign from per-carton from per-shipment. The engine
         charges it once per SIGN, which means an order of N signs carries N x US$21. If Cindy
         says the charge is per shipment, that over-recovers on multi-sign orders. */
      provisional.push('us_prepaid_tax_basis_unconfirmed');
    }

    var preFee = cents + taxcents + (fblocked ? 0 : fcents);
    var fee = Math.round(preFee * (m.handlingFactor - 1));
    var C = preFee + fee;

    return {
      lines: lines, componentsUSD: d(cents), usPrepaidTaxUSD: d(taxcents),
      powerSupplies: supplies,
      packing: { rule: plan.rule, divided: plan.divided, sections: plan.sections, across: plan.nx, down: plan.ny,
                 planSource: (POL && POL.sectionsFromGeometry && g.parts) ? 'catalogue geometry 1.2.0 (g.parts)' : undefined,
                 partsKey: (POL && POL.sectionsFromGeometry && g.parts) ? g.parts.key : undefined,
                 section_cm: plan.section_cm, carton_cm: plan.carton_cm, packages: 1, kgPerPackage: plan.kg,
                 usdPerPackage: plan.usd, basis: RATES.packing.basis, cartonDepth_cm: plan.carton_cm[2],
                 confirmed: false },
      freight: { packages: 1, packagingConfirmed: false, totalKg: Math.round(fkg * 100) / 100,
                 perPackage: [plan.freight], usd: d(fcents), blocked: false, extended: plan.extended, basis: plan.freight.basis },
      preFeeUSD: d(preFee), handlingUSD: d(fee), handlingFactor: m.handlingFactor,
      C_USD: fblocked || unresolved.length ? null : d(C),
      C_USD_raw: d(C), unresolved: unresolved, provisional: provisional
    };
  }

  /* ---- 2.6.9: the grade P size-records basis (see header) ----------------- */
  function recordsBase(g, cfg, market, outdoor, mfr) {
    var P = g.pricing, A = P && P[market], EPS = 1e-6, i, n, derived = false;
    /* 2.7.0: the UK has no recorded selling prices; scale the AU records by UK indoor / AU indoor */
    if ((!A || !A.length) && market === 'UK' && P && P.AU && P.AU.length) {
      var kUK = RATES.retail.UK.indoor / RATES.retail.AU.indoor;
      A = P.AU.map(function (r) { return [r[0], Math.round(r[1] * kUK * 100) / 100]; });
      derived = true;
    }
    if (!A || !A.length) return { error: 'records_missing_for_market' };
    n = A.length;
    for (i = 0; i < n; i++) if (!(A[i][0] > 0) || !(A[i][1] > 0)) return { error: 'records_missing_for_market' };
    for (i = 1; i < n; i++) if (!(A[i][0] > A[i - 1][0] + EPS) || !(A[i][1] >= A[i - 1][1])) return { error: 'records_not_increasing' };
    var w = g.board.w_mm, first = A[0], last = A[n - 1];
    if (!(w >= first[0] - EPS)) return { error: 'below_recorded_range' };
    var GEO = root.TNF_GEOM;
    var multIn = RATES.retail[market].indoor, multOut = RATES.retail[market].outdoor;
    /* the supplier cost model at another width of the same design (costing only: a recorded size is costed as it is) */
    function geomAt(x) {
      var gx = GEO && GEO.geometry(Object.assign({}, cfg, { width_mm: x, height_mm: null }));
      return gx && gx.tubing && gx.board ? gx : null;
    }
    function retail(gg, out) {
      if (!gg) return null;
      var mm = manufacturer(gg, Object.assign({}, cfg, { outdoor: !!out }), { rgbCost: false });
      return (mm.C_USD_raw != null && !mm.unresolved.length) ? mm.C_USD_raw * (out ? multOut : multIn) : null;
    }
    /* where w sits: on a record, between two, or above the largest */
    var seg, lo = 0, hi = 0, t = 0;
    if (w > last[0] + EPS) seg = 'above';
    else {
      for (i = 0; i < n - 1 && A[i + 1][0] < w - EPS; i++);
      lo = i; hi = Math.min(i + 1, n - 1);
      if (Math.abs(w - A[lo][0]) <= EPS) { seg = 'record'; hi = lo; }
      else if (Math.abs(w - A[hi][0]) <= EPS) { seg = 'record'; lo = hi; }
      else { seg = 'between'; t = (w - A[lo][0]) / (A[hi][0] - A[lo][0]); }
    }
    var out = { basis: 'records', market: market, width_mm: w, segment: seg, derivedFromAU: derived || undefined,
                anchors: A.map(function (a) { return [a[0], a[1]]; }),
                snapshot: P.snapshot || null, policy: P.policy || null };
    var gL = null, eInL = null;
    if (seg === 'above') {
      gL = geomAt(last[0]); eInL = retail(gL, false);
      var eInW = retail(g, false);
      if (eInW == null || eInL == null) return { error: 'records_growth_unpriceable' };
    }
    /* indoor: the recorded price, a straight line between records, and above the largest record its price plus the
       supplier's extra indoor cost of the larger sign at the indoor retail multiplier */
    var indoor = seg === 'above' ? last[1] + (eInW - eInL)
               : seg === 'record' ? A[lo][1]
               : A[lo][1] + (A[hi][1] - A[lo][1]) * t;
    if (seg === 'record') out.record = [A[lo][0], A[lo][1]];
    if (seg === 'between') out.between = [[A[lo][0], A[lo][1]], [A[hi][0], A[hi][1]]];
    if (seg === 'above') out.growth = { fromWidth_mm: last[0], fromPrice: last[1], retailInAtWidth: Math.round(eInW * 100) / 100,
                                        retailInAtLargestRecord: Math.round(eInL * 100) / 100, added: Math.round((eInW - eInL) * 100) / 100,
                                        rule: 'largest recorded price + (indoor retail of this engine at this width - at the largest recorded width)' };
    var amount = indoor;
    if (outdoor) {
      /* outdoor: at each record, its price x this engine's outdoor / indoor retail at that size (never below the outdoor
         price of a smaller record); a straight line between; above the largest record, the outdoor price there plus the
         supplier's extra OUTDOOR cost of the larger sign at the outdoor retail multiplier. Same shape as indoor. */
      var O = [];
      for (i = 0; i < n; i++) {
        var gi = geomAt(A[i][0]), ei = retail(gi, false), eo = retail(gi, true);
        if (ei == null || eo == null || !(ei > 0)) return { error: 'records_outdoor_unpriceable' };
        var oi = A[i][1] * eo / ei;
        if (i > 0 && oi < O[i - 1]) oi = O[i - 1];
        O.push(oi);
      }
      if (seg === 'above') {
        var eOutW = retail(g, true), eOutL = retail(gL, true);
        if (eOutW == null || eOutL == null) return { error: 'records_outdoor_unpriceable' };
        amount = O[n - 1] + (eOutW - eOutL);
        out.outdoorGrowth = { fromPrice: Math.round(O[n - 1] * 100) / 100, added: Math.round((eOutW - eOutL) * 100) / 100 };
      }
      else amount = seg === 'record' ? O[lo] : O[lo] + (O[hi] - O[lo]) * t;
      out.outdoorAnchors = A.map(function (a, k) { return [a[0], Math.round(O[k] * 100) / 100]; });
    }
    out.indoor = Math.round(indoor * 100) / 100;
    out.amount = Math.round(amount * 100) / 100;
    out.cents = Math.round(amount * 100);
    out.note = 'retail pricing policy from the product size records (grade P); not a measured tube length';
    return out;
  }

  /* ---- main ----------------------------------------------------------- */
  /* ---- the sale in force, if any (2026-09-27) --------------------------- */
  /* ---- ONE PLACE CONTROLS THE SALE (assets/tnf-promotion-1-0-0.js) -------
     The storefront loads that file as an asset; a service requires it from
     beside this engine. Whatever it says about percentages, dates, the
     Halloween list and the accessory prices wins over the built-in table
     below, so a percentage changed in one file changes the card, the product
     page, the creator, the cart and the checkout together - nobody edits
     three engines and hopes they stay in step. When the file is absent (an
     older deployment) the built-in values are used exactly as before. */
  var SETTINGS_READ = false, SETTINGS = null;
  function settings() {
    if (SETTINGS_READ) return SETTINGS;
    SETTINGS_READ = true;
    var S = (typeof root !== 'undefined' && root && root.TNF_PROMOTION_SETTINGS) || null;
    if (!S && typeof require === 'function') {
      try { S = require('./tnf-promotion-1-0-0.js'); } catch (e) { S = null; }
    }
    SETTINGS = S || null;
    if (SETTINGS && SETTINGS.accessories) {
      var A = SETTINGS.accessories, k;
      for (k in RATES.accessories) if (typeof A[k] === 'number') RATES.accessories[k] = A[k];
      if (A.wall_finish) for (k in RATES.wallMountKit) if (typeof A.wall_finish[k] === 'number') RATES.wallMountKit[k] = A.wall_finish[k];
    }
    return SETTINGS;
  }
  function promotionSettings() {
    var S = settings(), P = RATES.promotion;
    if (!S || !S.sale) return P;
    var s = S.sale, r = s.rates || {};
    return {
      enabled: s.enabled === true,
      id: s.id || P.id,
      title: s.title || 'Sale',
      halloweenTitle: s.halloweenTitle || 'Halloween Sale',
      startsAt: s.startsAt || null,
      endsAt: s.endsAt || null,
      rates: { halloween: r.halloween != null ? r.halloween : P.rates.halloween,
               standard:  r.standard  != null ? r.standard  : P.rates.standard,
               creator:   r.creator   != null ? r.creator   : (r.standard != null ? r.standard : P.rates.standard) },
      halloweenHandles: s.halloweenHandles || P.halloweenHandles,
      excludeKeys: s.excludeAccessories === false ? {} : P.excludeKeys,
      basis: 'assets/tnf-promotion-1-0-0.js'
    };
  }

  function promotionInForce() {
    var P = promotionSettings();
    if (!P) return null;
    var on = P.enabled === true;
    /* preview-only: set by snippets/tnf-promo-config.liquid, which emits
       nothing when the theme is live. No window on a server, so a service
       copy can only ever be switched on by its own enabled flag. It switches
       both ways, and assets/tnf-promo-1-2-0.js reads it the same way, so the
       badge on a card and the price from this engine cannot disagree while a
       preview is being reviewed. */
    var W = (typeof root !== 'undefined' && root) ? root.TNF_PROMOTION_OVERRIDE : null;
    if (W && W.enabled === true) on = true;
    if (W && W.enabled === false) on = false;
    if (!on) return null;
    var now = Date.now();
    if (P.startsAt && !(now >= Date.parse(P.startsAt))) return null;
    if (P.endsAt && !(now <= Date.parse(P.endsAt))) return null;
    return P;
  }
  function promotionFor(cfg) {
    var P = promotionInForce();
    if (!P) return null;
    var h = String((cfg && (cfg.handle || cfg.designHandle || cfg.design)) || '').toLowerCase().trim();
    var hal = !!h && P.halloweenHandles.indexOf(h) >= 0;
    /* a creator design sends no handle at all, and has its own rate */
    var tier = hal ? 'halloween' : (h ? 'standard' : 'creator');
    var rate = hal ? P.rates.halloween : (tier === 'creator' && P.rates.creator != null ? P.rates.creator : P.rates.standard);
    if (!(rate > 0 && rate < 1)) return null;
    return { id: P.id, tier: tier,
             title: hal ? (P.halloweenTitle || 'Halloween Sale') : (P.title || 'Sale'),
             rate: rate, percent: Math.round(rate * 1000) / 10,
             excludeKeys: P.excludeKeys || {} };
  }

  /* 2.7.1: the fixed retail tables for a market - the UK's own, or the shared AU/US ones */
  function fixedFor(market) {
    var U = market === 'UK' && RATES.marketFixed && RATES.marketFixed.UK;
    return {
      backboard:    U ? U.backboard    : RATES.backboard,
      finish:       U ? U.finish       : RATES.finish,
      wallMountKit: U ? U.wallMountKit : RATES.wallMountKit,
      accessories:  U ? U.accessories  : RATES.accessories,
      rgb:   U ? U.premiumRGB.rgb   : RATES.premiumRGB.rgb,
      rgb10: U ? U.premiumRGB.rgb10 : RATES.premiumRGB.rgb10,
      rgbwRoundTo: U ? U.premiumRGB.rgbwRoundTo : null,
      symbol: market === 'UK' ? '\u00a3' : '$'
    };
  }
  /* 2.7.1: what the page shows beside each option, from the same tables the quote charges */
  function fixedPrices(market) {
    settings();
    var F = fixedFor(market === 'US' ? 'US' : market === 'UK' ? 'UK' : 'AU');
    return { symbol: F.symbol, backboard: F.backboard, finish: F.finish, wallMountKit: F.wallMountKit,
             accessories: F.accessories, approved: market === 'UK' ? !!RATES.ukFixedApproved : true };
  }

  function quote(cfg) {
    var GEO = root.TNF_GEOM || (typeof require === 'function' ? require('./tnf-geometry-2-5-5.js') : null);
    /* 2.7.0: three markets. Anything that is not US or UK is AU, exactly as before. */
    var market = cfg.market === 'US' ? 'US' : cfg.market === 'UK' ? 'UK' : 'AU';
    var cur = market === 'US' ? 'USD' : market === 'UK' ? 'GBP' : 'AUD';

    var g = GEO.geometry(cfg);
    if (!g.ok) return { ok: false, reason: g.reason, unsupported: g.unsupported,
                        market: market, currency: cur, version: RATES.version };

    var isRGB = cfg.colourMode === 'rgb' || cfg.colourMode === 'rgbw';
    var rgbRule = isRGB ? RATES.premiumRGB.rule.selected : null;
    /* rule A costs the tube as single colour; B and C cost it as RGB */
    var mfr = manufacturer(g, cfg, { rgbCost: rgbRule !== 'A' });
    var flags = g.flags.slice();
    var blocking = mfr.unresolved.slice();
    if (g.flags.indexOf('above_market_maximum') >= 0) blocking.push('above_market_maximum');
    if (g.flags.indexOf('below_minimum_width') >= 0) blocking.push('below_minimum_width');
    /* a character the font cannot draw is never silently dropped from the price */
    if (g.unsupported && g.unsupported.length) blocking.push('unsupported_characters');
    /* 2.6.0: above 110 cm the sign is made in sections and priced through
       the packing model (mfr.packing); the provisional inputs travel with
       the quote instead of blocking it */
    var provisionalInputs = mfr.provisional.slice();
    if (g.parts && g.parts.divided) flags.push('divided_into_sections_over_110cm');

    var outdoor = !!cfg.outdoor;
    var mult = RATES.retail[market][outdoor ? 'outdoor' : 'indoor'];

    /* Premium RGB / RGBW: the live site's add-on TABLE is preserved exactly.
       How it combines with the cost model is not decided (see
       RATES.premiumRGB.rule): the live site added it to a size-card price that
       did not change with colour mode; this engine's manufacturer matrix
       costs RGB tubing at its own rate plus a controller. Every RGB quote is
       flagged provisional and carries all three candidate totals. */
    var protectedRGB = null;
    if (isRGB) {
      flags.push('premium_rgb_cost_basis_unconfirmed');
      var PR = RATES.premiumRGB, cls = g.fontClass, keys = PR.sizeKeys[cls] || PR.sizeKeys.fancy;
      var letterH = g.letterHeight_mm / 10, best = keys[0], dd = Infinity;
      for (var ki = 0; ki < keys.length; ki++) { var e = Math.abs(keys[ki][1] - letterH); if (e < dd) { dd = e; best = keys[ki]; } }
      var longText = String(cfg.text || '').replace(/\s/g, '').length >= PR.longTextChars;
      var FXR = fixedFor(market);
      var table = longText ? FXR.rgb10 : FXR.rgb;
      var baseAdd = (table[cls] || table.fancy)[best[0]];
      var amt = baseAdd != null ? (cfg.colourMode === 'rgbw' ? (FXR.rgbwRoundTo ? Math.round(baseAdd * PR.rgbwFactor / FXR.rgbwRoundTo) * FXR.rgbwRoundTo : baseAdd * PR.rgbwFactor) : baseAdd) : null;
      var comb = (longText ? PR.rgbOutdoorCombined10 : PR.rgbOutdoorCombined)[cls] || {};
      protectedRGB = { mode: cfg.colourMode, amount: amt, sizeKey: best[0], longText: longText, preserved: true,
                       rule: rgbRule, ruleApproved: !!RATES.premiumRGB.rule.approved,
                       liveOutdoorCombined: comb[best[0]] != null ? comb[best[0]] : null,
                       outdoorOverlapResolved: true };
      if (amt == null) blocking.push('premium_rgb_no_live_price_for_size');
      /* 2.6.6: outdoor colour-changing signs are sellable. Jack Goodwin
         (merchant) confirmed on 2026-09-22 that TNF RGBW signs are outdoor
         compatible and authorised the hold to be removed - a MERCHANT
         confirmation recorded here, not a manufacturer document inspected by
         this engine. The outdoor multiplier applies to the manufacturing cost
         and the premium is added once on top; the legacy combined outdoor
         figure below stays a diagnostic and is never charged. */
    }
    /* 2.6.6: was "&& !outdoor". The premium now applies indoors and outdoors,
       for RGB and for RGBW. The x1.5 RGBW factor is applied to the premium
       alone, above, and is untouched here - the sign is never multiplied. */
    var addOnApplies = protectedRGB && protectedRGB.amount != null && rgbRule !== 'C';

    var baseCents = mfr.C_USD_raw != null ? Math.round(mfr.C_USD_raw * mult * 100) : null;
    /* 2.6.9: a grade P design prices its sign from its size records (header); nothing else changes */
    var pricingBasis = null;
    if (g.pricing && g.pricing.basis === 'records' && market === 'UK') {
      /* 2.7.1 (Jack, 2 Oct 2026): UK-only quote-required handling for the 43 grade P designs -
         their recorded AU/US prices do not give a reliable UK cost, so the UK is never priced
         from them. AU and US are untouched. */
      blocking.push('uk_quote_required_cost_unverified');
    }
    if (g.pricing && g.pricing.basis === 'records' && market !== 'UK') {
      var rb = recordsBase(g, cfg, market, outdoor, mfr);
      if (rb.error) { blocking.push(rb.error); baseCents = null; pricingBasis = { basis: 'records', error: rb.error }; }
      else { baseCents = rb.cents; pricingBasis = rb; delete rb.cents;
             if (rb.derivedFromAU) provisionalInputs.push('uk_records_derived_from_au'); }
    }

    /* 6. fixed upgrades - added once, after the multiplier, never converted */
    var addOns = [], seen = {}, addCents = 0;
    function add(key, label, amt) {
      if (seen[key] || !amt) return; seen[key] = 1;
      addOns.push({ key: key, label: label, amount: amt, display: (market === 'UK' ? '\u00a3' : '$') + amt });
      addCents += c(amt);
    }
    var FXA = fixedFor(market);
    var bb = FXA.backboard[g.board.style];
    if (bb) add('backboard_' + g.board.style, labelOf(g.board.style), bb);
    var fin = FXA.finish[cfg.finish || 'clear'];
    if (fin) add('finish_' + cfg.finish, (cfg.finish.charAt(0).toUpperCase() + cfg.finish.slice(1)) + ' acrylic backboard', fin);
    if (cfg.wallMountingKit) {
      var wf = FXA.wallMountKit[cfg.wallMountFinish] != null ? cfg.wallMountFinish : 'silver';
      var wp = FXA.wallMountKit[wf];
      /* one kit, one charge: the finish REPLACES the amount (silver 0, black/gold 10) */
      add('wall_mounting_kit', 'Wall mounting kit (' + wf + ')', wp);
      if (!wp) addOns.push({ key: 'wall_mounting_kit', label: 'Wall mounting kit (' + wf + ')', amount: 0, display: 'Free' });
    }
    if (cfg.hangingKit) add('hanging_kit', 'Hanging kit', FXA.accessories.hanging_kit);
    if (cfg.tape3m)     add('tape_3m', '3M double-sided tape', FXA.accessories.tape_3m);
    /* 2.6.5: the customer is told what is actually included. A colour-changing
       sign ships with its controller and remote instead of the dimmer; the line
       is still free, and reads "Included" rather than "Free" so it does not look
       like a separate freebie. */
    if (cfg.freeDimmer !== false) addOns.push({ key: 'free_dimmer',
      label: isRGB ? (cfg.colourMode === 'rgbw' ? 'RGBW controller & remote' : 'RGB controller & remote')
                   : 'Free dimmer & remote',
      amount: 0, display: isRGB ? 'Included' : 'Free' });
    if (cfg.batteryPack) {
      /* charged once - add() refuses a repeated key, so the $40 cannot double */
      add('battery_pack', '12V battery pack', FXA.accessories.battery_pack);
      var bp = RATES.batteryPack;
      /* No size limit is asserted - see RATES.batteryPack. The only gate is
         Premium RGB / RGBW, and it is a stated judgement, not a measurement.
         Blocked ONLY when the customer has asked for a battery on a sign it is
         not offered for; the SIGN is never blocked - untick it and it sells. */
      var batteryColourOk = !bp.batteryOnSingleColourOnly || (cfg.colourMode || 'single') === 'single';
      if (!batteryColourOk) blocking.push('battery_not_offered_colour_mode');
      else flags.push('battery_runtime_unverified');
    }
    var otherAddCents = addCents;   // everything except the Premium RGB add-on
    if (addOnApplies)
      /* 2.6.5: the size key and the character-count note were internal detail
         leaking into a customer-visible line. */
      add('premium_' + cfg.colourMode, 'Premium ' + cfg.colourMode.toUpperCase() + ' upgrade', protectedRGB.amount);

    var qty = Math.max(1, parseInt(cfg.quantity || 1, 10));
    var unitCents = baseCents != null ? baseCents + addCents : null;

    /* ---- the sale, applied once, to the sign only (2026-09-27) ----------
       The accessories the customer chose are lifted out first and put back
       untouched afterwards, so the battery pack stays at its agreed 20 and a
       wall kit stays at its agreed price however deep the reduction is. The
       result then goes through the SAME single rounding as always - it is not
       a reduced version of a rounded price, which is how a sale ends up a cent
       away from what the cart charges. */
    var promo = promotionFor(cfg);
    var accessoryCents = 0;
    if (promo) {
      for (var ai = 0; ai < addOns.length; ai++)
        if (promo.excludeKeys ? promo.excludeKeys[addOns[ai].key] : RATES.promotion.excludeKeys[addOns[ai].key]) accessoryCents += c(addOns[ai].amount);
    }
    var regularUnitCents = unitCents;
    /* ONE rounding: the per-unit price is rounded to a whole dollar here and
       nowhere else. Everything downstream (cart, price list, sheet) sees it. */
    var unitWhole = unitCents != null ? Math.round(unitCents / 100) : null;
    var regularUnitWhole = unitWhole;
    /* The promotion is taken off the WHOLE-DOLLAR price the customer is shown,
       not off the cents behind it. A card in a collection knows only the price
       it prints, so this is the only rule a card, a product page, the cart and
       the checkout can all reproduce and all agree on - and it keeps the sale
       price a whole dollar, as every other price in this shop is. Accessories
       are excluded: they are taken out first and added back after. */
    if (promo && unitWhole != null) {
      var accessoryWhole = Math.round(accessoryCents / 100);
      var signWhole = unitWhole - accessoryWhole;
      /* To the nearest CENT, not the nearest dollar (Jack, 27 Sep): A$657 less
         25% is A$492.75, and A$995 less 25% plus a A$20 battery is A$766.25.
         The reduction is taken off the regular price the customer is shown, so
         a card, a product page, the creator, the cart and the checkout all
         reach the same figure from the same starting number. */
      if (signWhole > 0) unitWhole = Math.round(signWhole * (1 - promo.rate) * 100) / 100 + accessoryWhole;
      else promo = null;                                   /* nothing to reduce */
    }
    var payable = unitWhole != null ? Math.round(unitWhole * qty * 100) / 100 : null;
    var regularPayable = regularUnitWhole != null ? Math.round(regularUnitWhole * qty * 100) / 100 : null;

    /* The three candidate Premium RGB rules, each through the SAME rounding
       path, so they can be compared on one design. Review information only. */
    if (protectedRGB && !outdoor) {
      var mS = manufacturer(g, cfg, { rgbCost: false }), mR = manufacturer(g, cfg, { rgbCost: true });
      var rgbAdd = protectedRGB.amount != null ? c(protectedRGB.amount) : null;
      var unit = function (mm, withAdd) {
        if (mm.C_USD_raw == null || mm.unresolved.length || (withAdd && rgbAdd == null)) return null;
        return Math.round((Math.round(mm.C_USD_raw * mult * 100) + otherAddCents + (withAdd ? rgbAdd : 0)) / 100);
      };
      protectedRGB.candidates = {
        A: { perUnit: unit(mS, true),  C_USD: mS.C_USD_raw, addOn: protectedRGB.amount, note: RATES.premiumRGB.rule.candidates.A },
        B: { perUnit: unit(mR, true),  C_USD: mR.C_USD_raw, addOn: protectedRGB.amount, note: RATES.premiumRGB.rule.candidates.B },
        C: { perUnit: unit(mR, false), C_USD: mR.C_USD_raw, addOn: 0,                   note: RATES.premiumRGB.rule.candidates.C }
      };
      protectedRGB.candidates.selected = rgbRule;
    }

    /* 2.6.5 / 2.6.6: what to BUILD, kept separate from what the price is
       referenced to. The price keeps the single-colour base with the dimmer in
       it and adds the approved premium once; the sign itself is built with the
       controller named here and no dimmer. mode-aware since 2.6.6. */
    var mfgSpec = null;
    if (isRGB) {
      var MODE = cfg.colourMode.toUpperCase();
      var phys = manufacturer(g, cfg, { rgbCost: true });
      var pick = function (mm, k) { for (var i = 0; i < mm.lines.length; i++) if (mm.lines[i].k === k) return mm.lines[i]; return null; };
      var ctlLine = pick(phys, 'controller'), tubLine = pick(phys, 'tubing'), dimLine = pick(mfr, 'dimmer');
      mfgSpec = {
        colourMode: cfg.colourMode,
        controlGear: (outdoor ? 'Outdoor waterproof ' : '') + MODE + ' controller set',
        controlGearReplacesDimmer: true,
        tubing: MODE + (outdoor ? ' outdoor tube' : ' indoor tube'),
        physicalUSD: phys.C_USD_raw,
        pricingReferenceUSD: mfr.C_USD_raw,
        pricingReferenceBasis: (outdoor ? 'outdoor' : 'indoor') +
          ' single-colour retail base (dimmer retained) + approved ' + MODE +
          ' premium once - a PRICING REFERENCE, not the parts list',
        physicalLines: { controller: ctlLine ? ctlLine.usd : null,
                         rgbTubing: tubLine ? tubLine.usd : null,
                         dimmerInReference: dimLine ? dimLine.usd : null },
        note: 'The dimmer is priced but NOT fitted. The sign is built with the ' + MODE + ' controller named above.'
      };
    }

    var gst = (market === 'AU' && payable != null)
      ? Math.round((payable / 11) * 100) / 100 : null;
    /* 2.7.0: UK prices include VAT at 20% - the VAT inside the price is one sixth of it */
    var vat = (market === 'UK' && payable != null)
      ? Math.round((payable / 6) * 100) / 100 : null;
    if (market === 'UK' && !RATES.ukRetailApproved) provisionalInputs.push('uk_retail_multiplier_unapproved');
    if (market === 'UK' && !RATES.ukFixedApproved && (protectedRGB || addOns.some(function (a) { return a.amount > 0; }))) provisionalInputs.push('uk_fixed_prices_unapproved');

    return {
      ok: blocking.length === 0 && payable != null,
      version: RATES.version, rateSet: RATES.generated,
      market: market, currency: cur,
      quoteBlocked: blocking.length > 0 || payable == null,
      blocking: blocking, flags: flags,
      /* 2.6.0: inputs the price rests on that the manufacturer has not confirmed */
      provisional: provisionalInputs.length > 0,
      provisionalInputs: provisionalInputs.map(function (k) { return { code: k, what: PROVISIONAL[k] || k }; }),
      geometry: g, manufacturer: mfr,
      pricingBasis: pricingBasis,
      retail: { multiplier: mult, basis: outdoor ? 'outdoor' : 'indoor',
                amount: baseCents != null ? d(baseCents) : null },
      addOns: { items: addOns, total: d(addCents), currency: cur },
      protectedRGB: protectedRGB,
      manufacturingSpec: mfgSpec,
      perUnit: unitWhole, perUnitUnrounded: unitCents != null ? d(unitCents) : null,
      quantity: qty, payable: payable,
      /* the undiscounted price this sign would be without the sale - the only
         honest source for a struck-through "was", and what a compare-at price
         must be written from. Equal to payable when no sale is in force. */
      regularPerUnit: regularUnitWhole, regularPayable: regularPayable,
      promotion: promo ? { id: promo.id, tier: promo.tier, title: promo.title,
                           percent: promo.percent,
                           accessoriesExcluded: d(accessoryCents),
                           savedPerUnit: (regularUnitWhole != null && unitWhole != null) ? regularUnitWhole - unitWhole : null,
                           saved: (regularPayable != null && payable != null) ? regularPayable - payable : null } : null,
      tax: market === 'AU'
        ? { gstInclusive: true, component: gst, note: 'AU price includes GST' }
        : market === 'UK'
          ? { vatInclusive: true, component: vat, note: 'UK price includes VAT; never added on top' }
          : { salesTax: 'not applied in creator' },
      display: payable != null ? (market === 'UK' ? '\u00a3' : '$') + payable : null
    };
  }

  function labelOf(s) {
    return ({ cut_to_shape: 'Cut to shape', cut_to_rectangle: 'Cut to rectangle',
              cut_to_letter: 'Cut to letter', acrylic_stand: 'Acrylic stand',
              naked_cut: 'Naked cut', open_box: 'Open box' })[s] || s;
  }

  /* Every condition that makes a quote unpurchasable, with its basis. The order
     service refuses /order for any of these; the preview says "priced by hand". */
  var BLOCKING = {
    freight_above_rate_card:            'a carton beyond the 18.5 kg end of the supplied rate card - no longer blocking in 2.6.2, see freight_above_rate_card_fitted',
    freight_above_rate_card_fitted:     'the carton is heavier than the 18.5 kg end of the supplied rate card, so its freight is EXTRAPOLATED by continuing the card at its own final increment (US$10.00 per extra kg). No rate above the card has been supplied by the manufacturer. Against the 18 records that reach it the extrapolation is 1.17 x the actual freight at the median; the card below 18.5 kg is 0.82 x',
    above_market_maximum:               'wider than the market maximum (480 cm / 188 in)',
    below_minimum_width:                'narrower than the minimum for this wording (provisional rule)',
    outdoor_power_over_12m:             'outdoor sign over 12 m of tubing: manufacturer must size the power supply',
    uk_quote_required_cost_unverified:  'UK only: this catalogue design is priced from recorded AU/US selling prices that do not give a reliable UK cost, so UK customers are offered a quote instead of a price (2.7.1)',
    dimmer_at_exactly_5m:               'exactly 5 m of tubing: which dimmer applies is unconfirmed',
    unsupported_characters:             'a character the font cannot draw',
    premium_rgb_no_live_price_for_size: 'Premium RGB with no entry in the live add-on table for this size',
    outdoor_premium_rgb_rule_pending:   'outdoor + Premium RGB: how the outdoor and RGB charges combine is undecided',
    battery_not_offered_colour_mode:    '12V battery pack selected on a Premium RGB / RGBW sign: the colour controller\'s power draw has never been supplied, so the pack is not offered for those',
    battery_runtime_unverified:         '12V battery pack: the 8-12 hour runtime is the supplier\'s estimate and has never been checked against a manufacturer figure',
    battery_compatibility_unconfirmed:  '12V battery pack selected: the pack\'s capacity, output limit and which signs it can power are not confirmed by the supplier',
    /* 2.6.9: the size-records basis (grade P) */
    records_missing_for_market:         'a grade P design with no usable size records for this market',
    records_not_increasing:             'a grade P design whose recorded sizes or prices do not increase',
    below_recorded_range:               'narrower than the smallest recorded size of a grade P design',
    records_growth_unpriceable:         'above the largest recorded size, the supplier cost model could not price the extra size',
    records_outdoor_unpriceable:        'outdoor: the supplier cost model could not price the outdoor ratio at this size'
  };
  /* Inputs a computed price can rest on without manufacturer confirmation (2.6.0).
     They never block the quote; they travel with it (quote.provisionalInputs),
     are shown in the review strip and written to the production spec. */
  var PROVISIONAL = {
    oversize_sections_packing_provisional:   'over 110 cm the sign is made in sections, stacked into ONE carton and freighted once; the factory\'s exact cut points, packed depth and any joining charge are unconfirmed, and it is not confirmed whether a very large sign is ever split across two cartons',
    indoor_power_supplies_over_12m_assumed:  'more than 12 m of tubing: one indoor power supply per 12 m assumed from the outdoor tier structure',
    uv_print_board_area_assumed:             'the artwork is UV printed: the matrix UV print rate (US$40 per m2, basis unconfirmed) is charged on the board area (catalogue-1 policy; the printed area may be smaller than the board)',
    dimmer_at_exactly_5m_large_fitted:       'the tube is exactly 5 m: the matrix\'s small dimmer is for under 5 m and its large dimmer for over 5 m, so the large dimmer is fitted and charged (catalogue-1 policy, not a manufacturer statement)',
    outdoor_power_supplies_over_12m_assumed: 'outdoor sign over 12 m of tube: the tube is assumed to be wired as equal runs of at most 12 m, each with the supplied outdoor supply for its length, and one waterproof dimmer (catalogue-1 policy; the manufacturer says signs over 12 m must be recalculated)',
    uk_freight_card_unconfirmed:        'UK sign: the manufacturer\'s DHL card has not been supplied with a UK column, so the US column\'s freight stands in (2.7.0, PROVISIONAL)',
    uk_retail_multiplier_unapproved:    'UK sign: the UK retail multipliers (indoor 2.4, outdoor 2.8, GBP, VAT included) are a recommendation awaiting Jack\'s approval',
    uk_fixed_prices_unapproved:         'UK sign: the UK accessory, backboard and Premium RGB/RGBW amounts (about 0.75 x the AU/US number, clean GBP points) are prepared for preview testing and are not approved live prices (2.7.1)',
    uk_records_derived_from_au:         'UK grade P design: no UK selling records exist, so the AU records are scaled by UK indoor / AU indoor (2.4 / 4.5)',
    us_prepaid_tax_basis_unconfirmed:   'US prepaid import tax is charged once per SIGN (US$21). The only record we hold is a single sign in a single carton on a single shipment, so per-sign, per-carton and per-shipment cannot be told apart. An order of N signs is charged N x US$21.'
  };
  var API = { version: RATES.version, RATES: RATES, BLOCKING: BLOCKING, PROVISIONAL: PROVISIONAL, quote: quote, recordsBase: recordsBase, fixedPrices: fixedPrices,
              volumetricKg: volumetricKg, freightUSD: freightUSD, cartonPlan: cartonPlan, labelOf: labelOf };
  root.TNF_PRICE = API;
  if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof window !== 'undefined' ? window : globalThis);
